/**
 * Conversiemeting voor Google Ads / Google Analytics 4.
 *
 * Er staat één Google-tag op de site (gtag.js). Deze module stuurt daar
 * events naartoe. De Google Ads conversie-ID en de conversielabels worden via
 * omgevingsvariabelen ingesteld, zodat er geen tweede tag nodig is:
 *
 *   VITE_GOOGLE_ADS_ID              bijv. AW-123456789
 *   VITE_GOOGLE_ADS_BOOKING_LABEL   conversielabel van "Succesvolle boeking"
 *   VITE_GOOGLE_ADS_CALL_LABEL      conversielabel van "Klik op telefoonnummer"
 *   VITE_GOOGLE_ADS_WHATSAPP_LABEL  conversielabel van "Klik op WhatsApp"
 *   VITE_GOOGLE_ADS_CTA_LABEL       conversielabel van "Klik op Boek nu"
 *
 * Zolang een label leeg is, wordt alleen het GA4-event verstuurd. De
 * boekingsconversie wordt per boekingsnummer maximaal één keer gemeld, ook
 * als de bezoeker de pagina verlaat, terugkomt of verer.
 */

import { cameFromGoogleAds, getAttribution } from "./attribution";

type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: GtagFn;
    dataLayer?: unknown[];
    uetq?: unknown[];
    oaiq?: (...args: unknown[]) => void;
    taxiritjeInitOpenAiPixel?: () => void;
    taxiritjeInitAhrefs?: () => void;
    taxiritjeInitClarity?: () => void;
    /** Laadt de uitgestelde advertentie-/analysescripts direct (zie __root.tsx). */
    taxiritjeLoadTracking?: () => void;

  }
}

const env = import.meta.env as Record<string, string | undefined>;

/**
 * Google Ads-account. Conversielabels moeten per actie in Google Ads worden
 * aangemaakt en komen uit omgevingsvariabelen. Zolang een label leeg is,
 * wordt alleen het GA4-event verstuurd (geen Google Ads-conversie).
 */
export const GOOGLE_ADS_ID = env["VITE_GOOGLE_ADS_ID"] ?? "";

/**
 * Conversielabel van "Boeking (met waarde)" (Google Ads, aankoop met echte
 * ritprijs). Staat als vaste waarde in de code zodat de conversie ook meet
 * wanneer de omgevingsvariabele niet in de publicatie wordt meegegeven.
 */
const BOOKING_LABEL = "VcHDCLvqmfQcEMSvh8lE";

/** Conversielabel van "Beginnen met betalen" (Google Ads, begin_checkout). */
const CHECKOUT_LABEL = "fKACCNGh4PIcEMSvh8lE";

const LABELS = {
  booking: env["VITE_GOOGLE_ADS_BOOKING_LABEL"] || BOOKING_LABEL,
  call: env["VITE_GOOGLE_ADS_CALL_LABEL"] ?? "",
  whatsapp: env["VITE_GOOGLE_ADS_WHATSAPP_LABEL"] ?? "",
  cta: env["VITE_GOOGLE_ADS_CTA_LABEL"] ?? "",
  checkout: env["VITE_GOOGLE_ADS_CHECKOUT_LABEL"] || CHECKOUT_LABEL,
} as const;

type ConversionKey = keyof typeof LABELS;

function gtag(...args: unknown[]) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  if (typeof window.gtag === "function") window.gtag(...args);
  else window.dataLayer.push(args);
}

/**
 * De advertentie- en analysescripts worden bewust uitgesteld geladen (zie
 * __root.tsx) zodat ze de eerste weergave niet vertragen. Op momenten die er
 * echt toe doen — een consentkeuze of een bevestigde boeking — halen we ze
 * direct binnen, zodat er geen enkele meting verloren gaat.
 */
function loadTrackingNow() {
  if (typeof window === "undefined") return;
  try {
    window.taxiritjeLoadTracking?.();
  } catch {
    /* uitgestelde loader niet beschikbaar: wachtrijen blijven gewoon werken */
  }
}


/* ------------------------------------------------------------------ */
/* Google consent mode v2                                             */
/* ------------------------------------------------------------------ */

/**
 * Zet de Google-consenttoestand bij naar de keuze van de bezoeker. Vóór een
 * keuze staat alles op "denied" (zie het inline script in __root.tsx), zodat
 * gtag alleen geanonimiseerde pings verstuurt.
 */
export function applyConsent(prefs: { analytics: boolean }) {
  const state = prefs.analytics ? "granted" : "denied";
  gtag("consent", "update", {
    analytics_storage: state,
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  });
  // Microsoft Ads UET consent
  if (typeof window !== "undefined") {
    window.uetq = window.uetq || [];
    (window.uetq as unknown[]).push("consent", "update", { ad_storage: state });
    // De OpenAI Ads-pixel kent geen consent-modus: bij toestemming wordt hij
    // alsnog geladen, bij weigering nooit.
    if (prefs.analytics) {
      loadTrackingNow();
      window.taxiritjeInitOpenAiPixel?.();
      // Ook Ahrefs Analytics en Microsoft Clarity wachten op toestemming
      // (geen consent-modus).
      window.taxiritjeInitAhrefs?.();
      window.taxiritjeInitClarity?.();
    }


  }
  if (!prefs.analytics) clearTrackingCookies();
}

/**
 * Bij intrekken van toestemming verwijderen we de analyse- en
 * advertentiecookies die eerder zijn geplaatst. Google plaatst na een
 * "denied"-update geen nieuwe cookies meer, maar ruimt bestaande niet zelf op.
 */
function clearTrackingCookies() {
  if (typeof document === "undefined") return;
  const prefixes = ["_ga", "_gid", "_gcl", "_gac", "__gads", "_gat"];
  const host = window.location.hostname;
  const domains = new Set<string>([host, `.${host}`]);
  const parts = host.split(".");
  if (parts.length > 2) domains.add(`.${parts.slice(-2).join(".")}`);
  for (const cookie of document.cookie.split(";")) {
    const name = cookie.split("=")[0]?.trim();
    if (!name || !prefixes.some((p) => name.startsWith(p))) continue;
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
    }
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
}

/** Heeft de bezoeker advertentiecookies toegestaan? (voor enhanced conversions) */
function adConsentGranted() {
  try {
    const raw = window.localStorage.getItem("taxiritje-cookie-consent");
    if (!raw) return false;
    return (JSON.parse(raw) as { analytics?: boolean }).analytics === true;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ */
/* Enhanced conversions: SHA-256 van first-party gegevens             */
/* ------------------------------------------------------------------ */

async function sha256Hex(value: string) {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Normaliseert een e-mailadres volgens de regels van Google en Microsoft:
 * accenten en spaties weg, alles tussen "+" en "@" weg, punten voor de "@"
 * weg, kleine letters en geen punt aan het eind.
 */
function normalizeEmail(raw?: string | null) {
  if (!raw) return "";
  let email = raw
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .toLowerCase()
    .replace(/\.+$/, "");
  const at = email.lastIndexOf("@");
  if (at <= 0) return "";
  let local = email.slice(0, at);
  const domain = email.slice(at + 1);
  local = local.replace(/\+.*$/, "").replace(/\./g, "");
  if (!local || !domain) return "";
  email = `${local}@${domain}`;
  return email;
}

/** Telefoonnummer in E.164-formaat (Nederland als standaardland). */
function normalizePhone(raw?: string | null) {
  if (!raw) return "";
  const digits = raw.replace(/[^\d+]/g, "");
  if (!digits) return "";
  if (digits.startsWith("+")) return `+${digits.slice(1).replace(/\D/g, "")}`;
  if (digits.startsWith("00")) return `+${digits.slice(2)}`;
  if (digits.startsWith("31")) return `+${digits}`;
  return `+31${digits.replace(/^0+/, "")}`;
}

/**
 * Zet gehashte klantgegevens klaar voor Enhanced Conversions for Web
 * (Google Ads) en Microsoft Advertising UET (Enhanced Conversions).
 * Alleen aanroepen met gegevens die de klant zelf bij de boeking invulde en
 * alleen wanneer advertentieconsent is gegeven. Onbewerkte gegevens verlaten
 * de browser nooit: alles wordt eerst met SHA-256 gehasht.
 */
async function setUserData(email?: string | null, phone?: string | null) {
  if (typeof window === "undefined" || !crypto?.subtle) return;
  if (!adConsentGranted()) return;
  const data: Record<string, string> = {};
  const uet: Record<string, string> = {};
  const normalizedEmail = normalizeEmail(email);
  if (normalizedEmail) {
    const hash = await sha256Hex(normalizedEmail);
    data["sha256_email_address"] = hash;
    uet["em"] = hash;
  }
  const e164 = normalizePhone(phone);
  if (e164) {
    const hash = await sha256Hex(e164);
    data["sha256_phone_number"] = hash;
    uet["ph"] = hash;
  }
  if (Object.keys(data).length > 0) gtag("set", "user_data", data);
  if (Object.keys(uet).length > 0) {
    window.uetq = window.uetq || [];
    (window.uetq as unknown[]).push("set", { pid: uet });
  }
}

/* ------------------------------------------------------------------ */
/* Debug-uitvoer                                                      */
/* ------------------------------------------------------------------ */

export type TrackingLogEntry = {
  at: string;
  step: string;
  detail?: Record<string, unknown>;
};

/**
 * Houdt de laatste meetstappen bij in de browser, zodat we bij een testboeking
 * exact kunnen zien wat er is verstuurd. Zichtbaar via `window.taxiritjeTracking`
 * en in de console met `?trackdebug=1` in de URL (of localStorage-sleutel
 * `taxiritje-track-debug`).
 */
function debugEnabled() {
  if (typeof window === "undefined") return false;
  try {
    if (new URLSearchParams(window.location.search).get("trackdebug") === "1") {
      window.localStorage.setItem("taxiritje-track-debug", "1");
      return true;
    }
    return window.localStorage.getItem("taxiritje-track-debug") === "1";
  } catch {
    return false;
  }
}

export function trackingLog(step: string, detail?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const entry: TrackingLogEntry = {
    at: new Date().toISOString(),
    step,
    ...(detail ? { detail } : {}),
  };
  const w = window as unknown as { taxiritjeTracking?: TrackingLogEntry[] };
  w.taxiritjeTracking = [...(w.taxiritjeTracking ?? []).slice(-49), entry];
  if (debugEnabled()) console.info("[tracking]", entry.at, step, detail ?? "");
}

/** GA4-event, plus de Google Ads-conversie zodra het label is ingevuld. */
function track(eventName: string, key: ConversionKey, params: Record<string, unknown> = {}) {
  gtag("event", eventName, params);
  const label = LABELS[key];
  const sent = Boolean(GOOGLE_ADS_ID && label);
  if (sent) {
    gtag("event", "conversion", {
      send_to: `${GOOGLE_ADS_ID}/${label}`,
      ...params,
    });
  }
  trackingLog(`ga4_event:${eventName}`, {
    ads_conversion_sent: sent,
    ads_id: GOOGLE_ADS_ID || "(niet ingesteld)",
    ads_label: label || "(geen label ingesteld)",
    gtag_loaded: typeof window !== "undefined" && typeof window.gtag === "function",
    params,
  });
  if (!sent && GOOGLE_ADS_ID) {
    console.warn(
      `[tracking] Geen Google Ads-conversielabel voor "${key}". Er is alleen een GA4-event verstuurd.`,
    );
  }
}

/** GA4-gebeurtenis zonder Google Ads-conversie; uitsluitend voor funnelanalyse. */
function trackAnalyticsOnly(eventName: string, params: Record<string, unknown> = {}) {
  gtag("event", eventName, params);
  trackingLog(`ga4_event:${eventName}`, {
    ads_conversion_sent: false,
    analytics_only: true,
    params,
  });
}

/*
 * Let op: er wordt géén extra page_view naar Google Ads gestuurd op
 * /booking-success. De Google-tag verstuurt bij elke paginaweergave én bij
 * client-side navigatie al zelf een page_view met de echte URL naar het
 * Ads-account. Een tweede, handmatige page_view zou dezelfde URL-conversie
 * ("Aankoop") dubbel laten tellen.
 */

/* ------------------------------------------------------------------ */
/* Primaire conversie: succesvol afgeronde en betaalde boeking        */
/* ------------------------------------------------------------------ */

/**
 * Microsoft Advertising (UET) aankoopconversie. Wordt uitsluitend aangeroepen
 * vanuit `trackBookingConversion`, dus na een bevestigde betaling en maximaal
 * één keer per boekingsnummer. Staat de UET-tag (nog) niet op de pagina, dan
 * wordt het event in de wachtrij gezet; een fout mag de boekingsbevestiging en
 * de Google Ads-conversie nooit blokkeren.
 */
function trackMicrosoftPurchase(bookingNumber: string, value: number) {
  if (typeof window === "undefined") return;
  try {
    window.uetq = window.uetq || [];
    (window.uetq as unknown[]).push("event", "purchase", {
      revenue_value: value,
      currency: "EUR",
      transaction_id: bookingNumber,
    });
    trackingLog("uet_purchase", { transaction_id: bookingNumber, value, currency: "EUR" });
  } catch {
    /* UET niet beschikbaar: de rest van de meting gaat gewoon door */
  }
}

/**
 * OpenAI Ads aankoopconversie (pixel JSau5jRoDn1AbxPNBTPCvt). Wordt net als
 * de Microsoft-conversie alleen aangeroepen vanuit `trackBookingConversion`:
 * na bevestigde betaling en maximaal één keer per boekingsnummer. Zonder
 * advertentieconsent is de pixel niet geladen en wordt er niets verstuurd.
 * De gebeurtenisaanroep volgt de specificatie van OpenAI.
 */
function trackOpenAiPurchase(bookingNumber: string, value: number) {
  if (typeof window === "undefined") return;
  try {
    if (!adConsentGranted() || typeof window.oaiq !== "function") {
      trackingLog("openai_purchase_overgeslagen", {
        transaction_id: bookingNumber,
        reason: adConsentGranted() ? "pixel niet geladen" : "geen advertentieconsent",
      });
      return;
    }
    window.oaiq("measure", "order_created", { type: "contents" });
    trackingLog("openai_purchase", { transaction_id: bookingNumber, value, currency: "EUR" });
  } catch {
    /* OpenAI-pixel niet beschikbaar: de rest van de meting gaat gewoon door */
  }
}

const STORAGE_PREFIX = "tr_conv_booking_";

/** Is deze boeking al eerder als conversie gemeld in deze browser? */
function alreadyCounted(bookingNumber: string) {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + bookingNumber) !== null;
  } catch {
    return false;
  }
}

function markCounted(bookingNumber: string) {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + bookingNumber, String(Date.now()));
  } catch {
    /* privémodus zonder opslag: dan meten we hoogstens één keer extra */
  }
}

/**
 * Meldt de boekingsconversie precies één keer per boekingsnummer.
 * Alleen aanroepen wanneer de boeking daadwerkelijk is opgeslagen én betaald.
 */
export function trackBookingConversion(input: {
  bookingNumber: string;
  value: number;
  isReturn?: boolean;
  email?: string | null;
  phone?: string | null;
}) {
  if (typeof window === "undefined") return false;
  // Conversie: de uitgestelde advertentiescripts nu direct laden.
  loadTrackingNow();
  const attribution = getAttribution();

  trackingLog("boeking_bevestigd", {
    booking_number: input.bookingNumber,
    value: input.value,
    is_return: Boolean(input.isReturn),
    from_google_ads: cameFromGoogleAds(),
    attribution: attribution ?? "(geen herkomst bekend)",
    ad_consent: adConsentGranted(),
  });
  if (!input.bookingNumber || alreadyCounted(input.bookingNumber)) {
    trackingLog("conversie_overgeslagen", {
      booking_number: input.bookingNumber,
      reason: input.bookingNumber ? "al eerder gemeld in deze browser" : "geen boekingsnummer",
    });
    return false;
  }
  markCounted(input.bookingNumber);
  const value = Math.round(input.value * 100) / 100;
  const fire = () => {
    track("purchase", "booking", {
      transaction_id: input.bookingNumber,
      value,
      currency: "EUR",
      items: [
        {
          item_id: input.isReturn ? "taxirit-retour" : "taxirit-enkel",
          item_name: input.isReturn ? "Taxirit retour" : "Taxirit enkele rit",
          price: value,
          quantity: 1,
        },
      ],
    });
    trackMicrosoftPurchase(input.bookingNumber, value);
    trackOpenAiPurchase(input.bookingNumber, value);
  };

  // Enhanced conversions: eerst hashen, daarna het event. Mislukt hashen, dan
  // gaat het purchase-event alsnog gewoon door.
  void setUserData(input.email, input.phone).then(fire, fire);
  return true;
}

/* ------------------------------------------------------------------ */
/* Secundaire conversies                                              */
/* ------------------------------------------------------------------ */

const CHECKOUT_PREFIX = "tr_conv_checkout_";

/**
 * Meldt "Beginnen met betalen" precies één keer per boekingsnummer, zodra de
 * bezoeker een betaalmethode kiest. Secundair: stuurt geen biedingen aan.
 */
export function trackBeginCheckout(input: { bookingNumber: string; value: number }) {
  if (typeof window === "undefined") return false;
  loadTrackingNow();
  if (!input.bookingNumber) return false;
  try {
    if (window.localStorage.getItem(CHECKOUT_PREFIX + input.bookingNumber) !== null) {
      trackingLog("begin_checkout_overgeslagen", {
        booking_number: input.bookingNumber,
        reason: "al eerder gemeld in deze browser",
      });
      return false;
    }
    window.localStorage.setItem(CHECKOUT_PREFIX + input.bookingNumber, String(Date.now()));
  } catch {
    /* privémodus zonder opslag: dan meten we hoogstens één keer extra */
  }
  track("begin_checkout", "checkout", {
    transaction_id: input.bookingNumber,
    value: Math.round(input.value * 100) / 100,
    currency: "EUR",
  });
  return true;
}

/** Een vaste prijs is daadwerkelijk zichtbaar geworden voor de bezoeker. */
export function trackPriceCalculated(input: {
  value: number;
  passengers: number;
  locale: "nl" | "en";
}) {
  trackAnalyticsOnly("price_calculated", {
    value: Math.round(input.value * 100) / 100,
    currency: "EUR",
    passenger_count: input.passengers,
    language: input.locale,
  });
}

/** De bezoeker gaat na de prijs door naar het invullen van de boekingsgegevens. */
export function trackBookingDetailsStarted(input: {
  value: number | null;
  passengers: number;
  locale: "nl" | "en";
}) {
  trackAnalyticsOnly("booking_details_started", {
    ...(input.value == null ? {} : { value: Math.round(input.value * 100) / 100, currency: "EUR" }),
    passenger_count: input.passengers,
    language: input.locale,
  });
}

/** Klik op een telefoonnummer. */
export function trackPhoneClick(where: string) {
  track("phone_click", "call", { location: where });
}

/** Klik op een WhatsApp-link. */
export function trackWhatsappClick(where: string) {
  track("whatsapp_click", "whatsapp", { location: where });
}

/** Klik op de belangrijkste "Boek nu"-knop. */
export function trackBookCtaClick(where: string) {
  track("book_cta_click", "cta", { location: where });
}
